import React, {
  FunctionComponent,
  HTMLAttributes,
  useEffect,
  ChangeEventHandler,
} from 'react';

import { IconType } from 'react-icons';
import { HiFilter } from 'react-icons/hi';
import { useBoolean } from 'usehooks-ts';

import { Value } from '@/lib/constants';

import Text from './Text';

export interface SelectOption {
  label: string;
  value: string | number;
  className?: string;
  display?: string;
}
interface CriteriaSelectProps extends HTMLAttributes<HTMLSelectElement> {
  onChange: ChangeEventHandler<HTMLSelectElement>;
  displayLabel: string;
  icon?: IconType;
  options?: SelectOption[];
  value?: Value<string>;
  disabled?: boolean;
}

const CriteriaSelect: FunctionComponent<CriteriaSelectProps> = ({
  onChange,
  options,
  icon: Icon,
  displayLabel,
  className,
  value,
  disabled,
  ...props
}) => {
  const {
    value: hasOption,
    setTrue: setHasOptionTrue,
    setValue: setHasOptionValue,
  } = useBoolean(true);

  useEffect(() => {
    setHasOptionValue(!!value);
  }, [value, setHasOptionValue]);

  const handleOnChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    if (event.target.value !== undefined) {
      setHasOptionTrue();
    }
    onChange?.(event);
  };

  return (
    <div className="relative w-full">
      {/* Layer on top of the select */}
      <div className="absolute inset-0 flex gap-2 pl-[1.1rem] pointer-events-none items-center">
        {/* Icon spot */}
        <span
          className={`flex ${hasOption ? 'text-gray-600' : 'text-gray-600/70'}`}
        >
          {Icon ? <Icon size={20} /> : <HiFilter size={20} />}
        </span>
        {/* Label */}
        <span>
          <Text
            as="p"
            text={displayLabel}
            className={`text-gray-600 transition-all ${
              hasOption
                ? 'transfrom -translate-y-2 text-3xs'
                : 'text-xs md:text-sm'
            }`}
          ></Text>
        </span>
      </div>
      <select
        defaultValue={undefined}
        onChange={handleOnChange}
        value={value !== null && value !== undefined ? value : ''}
        disabled={disabled}
        className={`select pl-11 pt-3 w-full rounded-md focus:outline-none border-[1px] border-gray-300 text-xs md:text-sm font-bold 
        ${
          hasOption ? 'text-primary  bg-gray-200/70' : 'text-gray-700'
        } ${className}`}
        {...props}
      >
        <option value={undefined}></option>
        {options?.map((opt) => (
          <option value={opt.value} key={opt.value}>
            {opt.display || opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CriteriaSelect;
